import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
    Download, X as XIcon, RefreshCw, Sparkles, FileText, Lightbulb,
    FileCode, FileBarChart, ScrollText, ChevronDown, ChevronUp,
    ShieldCheck, AlertTriangle, Car,
} from "lucide-react";
import LabShell from "./LabShell";
import ScenarioVideoModal from "./ScenarioVideoModal";
import ClosedLoopTab from "./ClosedLoopTab";
import { useLabPageReady } from "./LabProgressBar";
import { Panel, StatusPill, formatRelative, formatDuration } from "./labUi";
import {
    fetchRun,
    fetchInboxItem,
    fetchRunReport,
    fetchRunReportMarkdown,
    fetchRunRecommendations,
    fetchRunAudit,
    stopRun,
    submitInbox,
    normalizeRunStatus,
    isBackendUnavailable,
    openRunEventStream,
} from "../../services/LabService";
import { labHref, navigate } from "./labRouter";
import PipelineTimeline from "./PipelineTimeline";

const POLL_INTERVAL_MS = 5000;
const TABS = [
    { key: "report",          label: "Report",          icon: FileText },
    { key: "audit",           label: "Reasoning Audit", icon: ShieldCheck },
    { key: "closed_loop",     label: "Closed-Loop",     icon: Car },
    { key: "recommendations", label: "Recommendations", icon: Lightbulb },
    { key: "experiments",     label: "Per-cell metrics",icon: FileBarChart },
    { key: "proposal",        label: "Proposal",        icon: FileCode },
    { key: "logs",            label: "States",          icon: ScrollText },
];

const isTerminal = (sfnStatus) => ["SUCCEEDED", "FAILED", "ABORTED", "TIMED_OUT"].includes(sfnStatus);

const fmt = (n, digits = 2) => (n === null || n === undefined ? "—" : Number(n).toFixed(digits));

const RecommendationCard = ({ rec }) => {
    const [showYaml, setShowYaml] = useState(false);
    const [pending, setPending] = useState(false);
    const [queued, setQueued] = useState(false);

    const queue = async () => {
        setPending(true);
        try {
            await submitInbox({
                yamlContent: rec.yaml || rec.raw_yaml,
                proposalId: rec.proposal_id || rec.id,
            });
            setQueued(true);
        } catch (err) {
            alert(err.message);
        } finally {
            setPending(false);
        }
    };

    return (
        <div className="border border-slate-200 dark:border-slate-800 rounded-lg p-4">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <h4 className="text-sm font-semibold">{rec.title || rec.proposal_id || rec.id}</h4>
                    {rec.rationale ? <p className="text-xs text-slate-500 mt-1">{rec.rationale}</p> : null}
                </div>
                <button
                    onClick={queue}
                    disabled={pending || queued}
                    className="flex-shrink-0 text-xs px-2.5 py-1 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
                >
                    {queued ? "Queued ✓" : pending ? "Queueing…" : "Queue this experiment"}
                </button>
            </div>
            <button
                onClick={() => setShowYaml((v) => !v)}
                className="mt-2 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-700"
            >
                {showYaml ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                {showYaml ? "Hide" : "Show"} YAML
            </button>
            {showYaml && (rec.yaml || rec.raw_yaml) ? (
                <pre className="mt-2 p-3 rounded-md bg-slate-900 text-slate-100 text-[11px] overflow-auto max-h-72">{rec.yaml || rec.raw_yaml}</pre>
            ) : null}
        </div>
    );
};

const Headline = ({ report }) => {
    if (!report?.experiments?.length) return null;
    const sorted = report.experiments
        .filter((e) => typeof e.metrics?.min_ade_mean === "number")
        .sort((a, b) => a.metrics.min_ade_mean - b.metrics.min_ade_mean);
    const best = sorted[0];
    if (!best) return (
        <div className="rounded-lg bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800 p-4 mb-4 text-sm text-slate-500">
            No successful invocations — every cell failed. See per-cell metrics below.
        </div>
    );
    return (
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-emerald-50 dark:from-indigo-950/40 dark:to-emerald-950/40 border border-indigo-200 dark:border-indigo-900 p-4 mb-4">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Best cell</div>
            <div className="text-sm">
                <span className="font-mono font-semibold">{best.name}</span>
                <span className="mx-2 text-slate-400">·</span>
                <span className="font-mono">min_ADE_mean: {fmt(best.metrics.min_ade_mean, 3)}</span>
                <span className="mx-2 text-slate-400">·</span>
                <span className="text-slate-500">{best.metrics.successes}/{best.metrics.n} ok</span>
            </div>
        </div>
    );
};

const ExperimentsTable = ({ report }) => {
    if (!report?.experiments?.length) {
        return <div className="text-sm text-slate-500">No per-cell metrics in this report.</div>;
    }
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-xs">
                <thead className="text-[10px] text-slate-500 uppercase tracking-wide">
                    <tr className="border-b border-slate-200 dark:border-slate-800">
                        <th className="text-left px-2 py-2 font-medium">Cell</th>
                        <th className="text-left px-2 py-2 font-medium">Decoding</th>
                        <th className="text-right px-2 py-2 font-medium">N</th>
                        <th className="text-right px-2 py-2 font-medium">ok / fail</th>
                        <th className="text-right px-2 py-2 font-medium">min_ADE mean</th>
                        <th className="text-right px-2 py-2 font-medium">p50</th>
                        <th className="text-right px-2 py-2 font-medium">p95</th>
                        <th className="text-right px-2 py-2 font-medium">latency p95</th>
                        <th className="text-right px-2 py-2 font-medium">cost</th>
                    </tr>
                </thead>
                <tbody>
                    {report.experiments.map((exp) => (
                        <tr key={exp.name} className="border-b border-slate-100 dark:border-slate-800">
                            <td className="px-2 py-2 font-mono text-slate-900 dark:text-slate-100">{exp.name}</td>
                            <td className="px-2 py-2 font-mono text-slate-500">
                                {exp.decoding
                                    ? Object.entries(exp.decoding).map(([k, v]) => `${k}=${v}`).join(" ")
                                    : "—"}
                            </td>
                            <td className="px-2 py-2 text-right font-mono">{exp.metrics?.n ?? "—"}</td>
                            <td className="px-2 py-2 text-right font-mono">
                                <span className="text-emerald-600">{exp.metrics?.successes ?? 0}</span>
                                /
                                <span className="text-red-500">{exp.metrics?.failures ?? 0}</span>
                            </td>
                            <td className="px-2 py-2 text-right font-mono">{fmt(exp.metrics?.min_ade_mean, 3)}</td>
                            <td className="px-2 py-2 text-right font-mono">{fmt(exp.metrics?.min_ade_p50, 3)}</td>
                            <td className="px-2 py-2 text-right font-mono">{fmt(exp.metrics?.min_ade_p95, 3)}</td>
                            <td className="px-2 py-2 text-right font-mono">{fmt(exp.metrics?.latency_p95_seconds, 1)}s</td>
                            <td className="px-2 py-2 text-right font-mono">${fmt(exp.metrics?.cost_usd_estimate, 2)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// ─── Wedge 2: Reasoning Audit components ───────────────────────────────
const pct = (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);

const AuditHeadline = ({ aggregate }) => {
    if (!aggregate || aggregate.n === 0) return null;
    const consistency = aggregate.reasoning_action_consistency_rate;
    const grounded = aggregate.decision_groundedness_rate;
    const failures = aggregate.consistency_failure_count || 0;

    let tone = "emerald";
    if (consistency != null && consistency < 0.7) tone = "amber";
    if (consistency != null && consistency < 0.4) tone = "red";

    const toneClasses = {
        emerald: "from-emerald-50 to-teal-50 dark:from-emerald-950/40 dark:to-teal-950/40 border-emerald-200 dark:border-emerald-900",
        amber:   "from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/40 border-amber-200 dark:border-amber-900",
        red:     "from-red-50 to-rose-50 dark:from-red-950/40 dark:to-rose-950/40 border-red-200 dark:border-red-900",
    };

    return (
        <div className={`rounded-lg bg-gradient-to-br ${toneClasses[tone]} border p-4 mb-4`}>
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5" /> Headline numbers
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                    <div className="text-2xl font-mono font-bold">{aggregate.n}</div>
                    <div className="text-xs text-slate-500">Invocations audited</div>
                </div>
                <div>
                    <div className="text-2xl font-mono font-bold">{pct(grounded)}</div>
                    <div className="text-xs text-slate-500">Decision-grounded CoT</div>
                </div>
                <div>
                    <div className="text-2xl font-mono font-bold">{pct(consistency)}</div>
                    <div className="text-xs text-slate-500">Reasoning ⇔ trajectory consistency</div>
                </div>
                <div>
                    <div className="text-2xl font-mono font-bold">{failures}</div>
                    <div className="text-xs text-slate-500">Audit-worthy failures</div>
                </div>
            </div>
        </div>
    );
};

const AuditDistribution = ({ title, dist, total }) => {
    if (!dist || Object.keys(dist).length === 0) return null;
    return (
        <div className="border border-slate-200 dark:border-slate-800 rounded-lg p-4 mb-3">
            <h4 className="text-sm font-semibold mb-3">{title}</h4>
            <div className="space-y-2">
                {Object.entries(dist).map(([k, v]) => {
                    const widthPct = total ? (v / total) * 100 : 0;
                    return (
                        <div key={k} className="flex items-center gap-2 text-xs">
                            <span className="font-mono w-44 truncate">{k}</span>
                            <div className="flex-1 h-2 bg-slate-100 dark:bg-slate-800 rounded overflow-hidden">
                                <div
                                    className="h-full bg-indigo-400 dark:bg-indigo-600"
                                    style={{ width: `${widthPct}%` }}
                                />
                            </div>
                            <span className="font-mono w-12 text-right text-slate-500">{v} ({widthPct.toFixed(0)}%)</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const AuditFailures = ({ failures, onOpenVideo }) => {
    if (!failures || failures.length === 0) {
        return (
            <div className="text-sm text-emerald-700 dark:text-emerald-300 flex items-center gap-2 py-2">
                <ShieldCheck className="w-4 h-4" />
                No consistency failures — every CoT-stated decision matched the trajectory.
            </div>
        );
    }
    return (
        <div className="border border-amber-200 dark:border-amber-900 rounded-lg p-4 bg-amber-50/40 dark:bg-amber-950/20">
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                {failures.length} consistency failure{failures.length === 1 ? "" : "s"}
            </h4>
            <p className="text-xs text-slate-600 dark:text-slate-400 mb-3">
                These are scenarios where the model's stated reasoning didn't match the predicted
                trajectory — the cases regulators and safety teams care about.
                <span className="text-indigo-600 dark:text-indigo-400 ml-1">
                    Click any scenario to watch the scene play out around t0.
                </span>
            </p>
            <div className="space-y-2 max-h-96 overflow-auto">
                {failures.slice(0, 50).map((f, i) => (
                    <button
                        key={`${f.scenario_id}-${i}`}
                        onClick={() => onOpenVideo?.(f.scenario_id, f.notes || [])}
                        className="block w-full text-left text-xs border-l-2 border-amber-400 pl-3 py-1.5 rounded-r hover:bg-amber-100/40 dark:hover:bg-amber-900/20 transition group cursor-pointer"
                    >
                        <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="font-mono text-slate-700 dark:text-slate-200 group-hover:text-indigo-700 dark:group-hover:text-indigo-300">
                                {f.scenario_id}
                            </span>
                            <span className="text-[10px] text-indigo-600 dark:text-indigo-400 opacity-0 group-hover:opacity-100 transition">
                                ▶ play
                            </span>
                        </div>
                        {(f.notes || []).map((note, j) => (
                            <div key={j} className="text-slate-600 dark:text-slate-400">{note}</div>
                        ))}
                    </button>
                ))}
                {failures.length > 50 ? (
                    <div className="text-xs text-slate-500 italic">
                        Showing first 50 of {failures.length}. Full list in audit/run_aggregate.json.
                    </div>
                ) : null}
            </div>
        </div>
    );
};

const AuditPerExperiment = ({ perExperiment }) => {
    if (!perExperiment || Object.keys(perExperiment).length === 0) return null;
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-xs">
                <thead className="text-[10px] text-slate-500 uppercase tracking-wide">
                    <tr className="border-b border-slate-200 dark:border-slate-800">
                        <th className="text-left px-2 py-2 font-medium">Experiment cell</th>
                        <th className="text-right px-2 py-2 font-medium">N</th>
                        <th className="text-right px-2 py-2 font-medium">Grounded %</th>
                        <th className="text-right px-2 py-2 font-medium">Consistent %</th>
                        <th className="text-right px-2 py-2 font-medium">Long %</th>
                        <th className="text-right px-2 py-2 font-medium">Lat %</th>
                        <th className="text-right px-2 py-2 font-medium">Failures</th>
                    </tr>
                </thead>
                <tbody>
                    {Object.entries(perExperiment).map(([name, data]) => {
                        const a = data?.aggregate || {};
                        return (
                            <tr key={name} className="border-b border-slate-100 dark:border-slate-800">
                                <td className="px-2 py-2 font-mono text-slate-900 dark:text-slate-100">{name}</td>
                                <td className="px-2 py-2 text-right font-mono">{a.n ?? "—"}</td>
                                <td className="px-2 py-2 text-right font-mono">{pct(a.decision_groundedness_rate)}</td>
                                <td className="px-2 py-2 text-right font-mono">{pct(a.reasoning_action_consistency_rate)}</td>
                                <td className="px-2 py-2 text-right font-mono">{pct(a.longitudinal_consistency_rate)}</td>
                                <td className="px-2 py-2 text-right font-mono">{pct(a.lateral_consistency_rate)}</td>
                                <td className="px-2 py-2 text-right font-mono">{a.consistency_failure_count ?? 0}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};


const LabExperimentDetail = ({ runId }) => {
    const [run, setRun] = useState(null);
    const [proposal, setProposal] = useState(null);
    const [report, setReport] = useState(null);
    const [reportMd, setReportMd] = useState(null);
    const [recommendations, setRecommendations] = useState([]);
    const [audit, setAudit] = useState(null);
    const [videoModal, setVideoModal] = useState(null); // { scenarioId, notes } | null
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState("report");
    const [lastUpdated, setLastUpdated] = useState(null);
    const pollTimer = useRef(null);
    const eventSource = useRef(null);

    // Keep the top progress bar visible until the run header loads (or an
    // error pins the page). The report is fetched in parallel but the page
    // is "usable" as soon as `run` is set.
    useLabPageReady(run === null && error === null);

    const loadRun = useCallback(async () => {
        try {
            const next = await fetchRun(runId);
            setRun(next);
            setLastUpdated(new Date());
            return next;
        } catch (err) {
            if (isBackendUnavailable(err)) {
                setRun(null);
            } else {
                setError(err);
            }
            return null;
        }
    }, [runId]);

    const loadProposal = useCallback(async (proposalId) => {
        if (!proposalId) return;
        try {
            const p = await fetchInboxItem(proposalId);
            setProposal(p);
        } catch (err) {
            console.warn("[Lab] proposal fetch failed:", err.message);
        }
    }, []);

    const loadReport = useCallback(async () => {
        try {
            const [json, md, recs, auditData] = await Promise.all([
                fetchRunReport(runId),
                fetchRunReportMarkdown(runId).catch(() => null),
                fetchRunRecommendations(runId).catch(() => ({ items: [] })),
                fetchRunAudit(runId).catch(() => null),
            ]);
            setReport(json);
            setReportMd(md);
            setRecommendations(recs?.items || recs || []);
            setAudit(auditData);
        } catch (err) {
            console.warn("[Lab] report fetch failed:", err.message);
        }
    }, [runId]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const next = await loadRun();
            if (cancelled || !next) return;
            if (next.proposal_id) loadProposal(next.proposal_id);
            if (next.report_available || isTerminal(next.status)) {
                loadReport();
            }
        })();
        return () => { cancelled = true; };
    }, [runId, loadRun, loadProposal, loadReport]);

    // Polling fallback while running. SSE is the preferred channel per
    // backend §4.7 but the docs themselves call for polling as a backup.
    useEffect(() => {
        if (pollTimer.current) {
            clearInterval(pollTimer.current);
            pollTimer.current = null;
        }
        if (!run || isTerminal(run.status)) {
            if (run && isTerminal(run.status) && !report) loadReport();
            return;
        }
        pollTimer.current = setInterval(async () => {
            const next = await loadRun();
            if (next && isTerminal(next.status)) {
                clearInterval(pollTimer.current);
                pollTimer.current = null;
                loadReport();
            }
        }, POLL_INTERVAL_MS);
        return () => {
            if (pollTimer.current) {
                clearInterval(pollTimer.current);
                pollTimer.current = null;
            }
        };
    }, [run, report, loadRun, loadReport]);

    // SSE live channel. Closes itself when the server emits "terminal".
    useEffect(() => {
        if (!run || isTerminal(run.status)) return undefined;
        try {
            const es = openRunEventStream(runId);
            eventSource.current = es;
            es.addEventListener("state_transition", () => loadRun());
            es.addEventListener("terminal", () => {
                loadRun().then(() => loadReport());
                es.close();
            });
            es.onerror = () => {
                // Network/proxy hiccup — let the polling effect carry us.
                es.close();
            };
            return () => es.close();
        } catch (_) {
            return undefined;
        }
    }, [run, runId, loadRun, loadReport]);

    const uiStatus = run ? normalizeRunStatus(run.status) : "queued";

    const handleStop = async () => {
        if (!window.confirm("Stop this run? It can't be resumed.")) return;
        try {
            await stopRun(runId);
            loadRun();
        } catch (err) {
            alert(err.message);
        }
    };

    const downloadYaml = () => {
        const yaml = proposal?.raw_yaml;
        if (!yaml) return;
        const blob = new Blob([yaml], { type: "application/yaml" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${proposal?.proposal_id || runId}.yaml`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const headerAction = useMemo(() => (
        <div className="flex items-center gap-2 text-xs">
            {lastUpdated ? (
                <span className="text-slate-500">Updated {formatRelative(lastUpdated.toISOString())}</span>
            ) : null}
            {uiStatus === "running" ? (
                <button
                    onClick={handleStop}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                    <XIcon className="w-3.5 h-3.5" /> Stop
                </button>
            ) : null}
            {proposal?.raw_yaml ? (
                <button
                    onClick={downloadYaml}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                    <Download className="w-3.5 h-3.5" /> YAML
                </button>
            ) : null}
            <button
                onClick={() => { loadRun(); if (run && isTerminal(run.status)) loadReport(); }}
                title="Refresh"
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
            >
                <RefreshCw className="w-3.5 h-3.5" />
            </button>
        </div>
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [run, proposal, lastUpdated, uiStatus]);

    return (
        <LabShell
            activeKey="experiments"
            breadcrumbExtra={runId}
            headerAction={headerAction}
        >
            {error ? (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error.message}</div>
            ) : null}

            {!run ? (
                <div className="text-sm text-slate-500">Loading run…</div>
            ) : (
                <>
                    <div className="mb-4 flex items-start justify-between gap-4">
                        <div>
                            <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-50">
                                {run.proposal_id || run.run_id}
                            </h1>
                            <div className="mt-1 flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                                <StatusPill status={uiStatus} />
                                {run.current_state ? (
                                    <span>· last state: <span className="font-medium text-slate-700 dark:text-slate-200">{run.current_state}</span></span>
                                ) : null}
                                <span>· started {formatRelative(run.started_at)}</span>
                                {run.stopped_at ? <span>· ended {formatRelative(run.stopped_at)}</span> : null}
                                {run.duration_seconds != null ? <span>· {formatDuration(run.duration_seconds * 1000)}</span> : null}
                                <span className="font-mono">· {run.run_id}</span>
                            </div>
                        </div>
                    </div>

                    {run.failure_cause ? (
                        <div className="mb-4 rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30 p-3 text-sm text-red-800 dark:text-red-200">
                            <div className="font-semibold mb-1">Failure cause</div>
                            <pre className="text-xs whitespace-pre-wrap">{run.failure_cause}</pre>
                        </div>
                    ) : null}

                    <div className="mb-6">
                        <PipelineTimeline states={run.states || []} />
                    </div>

                    <div className="mb-4 flex items-center gap-1 border-b border-slate-200 dark:border-slate-800 overflow-x-auto">
                        {TABS.map((t) => {
                            const Icon = t.icon;
                            return (
                                <button
                                    key={t.key}
                                    onClick={() => setActiveTab(t.key)}
                                    className={`flex items-center gap-1.5 px-3 py-2 text-xs border-b-2 -mb-px transition flex-shrink-0 ${
                                        activeTab === t.key
                                            ? "border-indigo-500 text-indigo-700 dark:text-indigo-300 font-medium"
                                            : "border-transparent text-slate-500 hover:text-slate-700"
                                    }`}
                                >
                                    <Icon className="w-3.5 h-3.5" />
                                    {t.label}
                                    {t.key === "recommendations" && recommendations.length > 0 ? (
                                        <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-100 text-amber-700 text-[10px]">
                                            {recommendations.length}
                                        </span>
                                    ) : null}
                                </button>
                            );
                        })}
                    </div>

                    {activeTab === "report" ? (
                        <Panel>
                            {!report && !reportMd ? (
                                <div className="text-sm text-slate-500">
                                    {isTerminal(run.status) ? "Loading report…" : "The report will appear here once the run completes."}
                                </div>
                            ) : (
                                <>
                                    <Headline report={report} />
                                    {reportMd ? (
                                        <div className="prose prose-sm dark:prose-invert max-w-none">
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{reportMd}</ReactMarkdown>
                                        </div>
                                    ) : null}
                                </>
                            )}
                        </Panel>
                    ) : null}

                    {activeTab === "audit" ? (
                        <Panel
                            title={
                                <span className="flex items-center gap-2">
                                    <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
                                    Reasoning Audit
                                    <span className="text-[10px] font-normal text-slate-500 uppercase tracking-wide">Wedge 2</span>
                                </span>
                            }
                        >
                            {!audit && !isTerminal(run.status) ? (
                                <div className="text-sm text-slate-500">
                                    The reasoning audit will appear here once the run completes.
                                </div>
                            ) : !audit ? (
                                <div className="text-sm text-slate-500">
                                    No audit data available for this run yet.
                                </div>
                            ) : (
                                <>
                                    <p className="text-xs text-slate-600 dark:text-slate-400 mb-4">
                                        Each invocation's chain-of-thought was parsed into the 15-decision
                                        vocabulary from NVIDIA's Alpamayo-R1 paper (Table 1), then compared
                                        to what the trajectory actually did. Use this to find scenarios where
                                        the model says one thing and does another.
                                    </p>
                                    <AuditHeadline aggregate={audit.aggregate} />
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
                                        <AuditDistribution
                                            title="Longitudinal decisions"
                                            dist={audit.aggregate?.longitudinal_decision_distribution}
                                            total={audit.aggregate?.n}
                                        />
                                        <AuditDistribution
                                            title="Lateral decisions"
                                            dist={audit.aggregate?.lateral_decision_distribution}
                                            total={audit.aggregate?.n}
                                        />
                                    </div>
                                    <div className="mb-4">
                                        <AuditFailures
                                            failures={audit.aggregate?.consistency_failures}
                                            onOpenVideo={(scenarioId, notes) =>
                                                setVideoModal({ scenarioId, notes })
                                            }
                                        />
                                    </div>
                                    {audit.per_experiment && Object.keys(audit.per_experiment).length > 0 ? (
                                        <div>
                                            <h4 className="text-sm font-semibold mb-2">Per-experiment breakdown</h4>
                                            <AuditPerExperiment perExperiment={audit.per_experiment} />
                                        </div>
                                    ) : null}
                                </>
                            )}
                        </Panel>
                    ) : null}

                    {activeTab === "closed_loop" ? (
                        <Panel
                            title={
                                <span className="flex items-center gap-2">
                                    <Car className="w-3.5 h-3.5 text-indigo-500" />
                                    Closed-Loop (AlpaSim)
                                    <span className="text-[10px] font-normal text-slate-500 uppercase tracking-wide">Wedge 4</span>
                                </span>
                            }
                        >
                            <ClosedLoopTab runId={runId} />
                        </Panel>
                    ) : null}

                    {activeTab === "recommendations" ? (
                        <Panel
                            title={
                                <span className="flex items-center gap-2">
                                    <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                                    {recommendations.length} recommendation{recommendations.length === 1 ? "" : "s"}
                                </span>
                            }
                        >
                            {recommendations.length === 0 ? (
                                <div className="text-sm text-slate-500">
                                    No follow-up suggestions for this run.
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {recommendations.map((rec) => (
                                        <RecommendationCard key={rec.proposal_id || rec.id || rec.title} rec={rec} />
                                    ))}
                                </div>
                            )}
                        </Panel>
                    ) : null}

                    {activeTab === "experiments" ? (
                        <Panel title="Per-cell metrics">
                            <ExperimentsTable report={report} />
                        </Panel>
                    ) : null}

                    {activeTab === "proposal" ? (
                        <Panel
                            title={proposal ? `Proposal · ${proposal.proposal_id}` : "Proposal"}
                            action={
                                <button
                                    onClick={() => navigate(labHref("submit"))}
                                    className="text-xs text-indigo-600 hover:text-indigo-700"
                                >
                                    Resubmit as new →
                                </button>
                            }
                        >
                            {proposal?.raw_yaml ? (
                                <pre className="p-3 rounded-md bg-slate-900 text-slate-100 text-xs overflow-auto max-h-[60vh]">{proposal.raw_yaml}</pre>
                            ) : (
                                <div className="text-sm text-slate-500">YAML not available.</div>
                            )}
                        </Panel>
                    ) : null}

                    {activeTab === "logs" ? (
                        <Panel title="State transitions">
                            {(run.states || []).length === 0 ? (
                                <div className="text-sm text-slate-500">No state history yet.</div>
                            ) : (
                                <pre className="p-3 rounded-md bg-slate-950 text-emerald-200 text-[11px] font-mono overflow-auto max-h-[60vh]">
                                    {(run.states || []).map((s) =>
                                        `[${s.state || s.name}] status=${s.status || "?"}` +
                                        (s.entered_at ? ` entered=${s.entered_at}` : "") +
                                        (s.exited_at ? ` exited=${s.exited_at}` : "") +
                                        "\n"
                                    ).join("")}
                                </pre>
                            )}
                        </Panel>
                    ) : null}
                </>
            )}

            {videoModal ? (
                <ScenarioVideoModal
                    scenarioId={videoModal.scenarioId}
                    notes={videoModal.notes}
                    onClose={() => setVideoModal(null)}
                />
            ) : null}
        </LabShell>
    );
};

export default LabExperimentDetail;
